export interface HistoricalSyncResponse {
  synced: number;
  total_fetched: number;
  errors: string[];
  status: "ok" | "partial";
  history: Record<string, unknown>;
}

/** Reconnecting a different athlete requires an explicit new interval. */
export function stravaHistoryForAthlete(connection: {
  provider_user_id: string | null;
  provider_metadata: Record<string, unknown> | null;
} | null): unknown {
  const history = connection?.provider_metadata?.history_sync;
  if (!history || typeof history !== "object" || Array.isArray(history)) return undefined;
  return (history as Record<string, unknown>).provider_user_id === connection?.provider_user_id
    ? history : undefined;
}

/** An old/invalid deployed service cannot attest a historical import. */
export function parseHistoricalSyncResponse(value: unknown): HistoricalSyncResponse {
  const fail = () => { throw new Error("history_response_unavailable"); };
  if (!value || typeof value !== "object" || Array.isArray(value)) return fail();
  const result = value as Record<string, unknown>;
  const count = (v: unknown) => typeof v === "number" && Number.isSafeInteger(v) && v >= 0;
  if (!count(result.synced) || !count(result.total_fetched) || !Array.isArray(result.errors)
    || result.errors.length > 100 || !result.errors.every(e => typeof e === "string" && e.length <= 2000)
    || !["ok", "partial"].includes(String(result.status))) return fail();
  const history = result.history;
  if (!history || typeof history !== "object" || Array.isArray(history)) return fail();
  const h = history as Record<string, unknown>;
  const date = (v: unknown) => typeof v === "string" && v.length === 24
    && Number.isFinite(Date.parse(v)) && new Date(v).toISOString() === v;
  if (h.version !== 1 || !date(h.from) || !date(h.to) || !date(h.next_before)
    || !count(h.completed_windows) || !["in_progress", "complete", "partial", "blocked"].includes(String(h.status))) return fail();
  const from = Date.parse(String(h.from)), to = Date.parse(String(h.to)), next = Date.parse(String(h.next_before));
  if (from >= to || next < from || next > to || (h.status === "complete" && next !== from)) return fail();
  if (h.status === "partial" || h.status === "blocked") {
    if (result.status !== "partial") return fail();
  }
  return { synced: result.synced as number, total_fetched: result.total_fetched as number,
    errors: result.errors as string[], status: result.status as "ok" | "partial", history: h };
}
